import { html, safeUrl, cssToken } from 'html-utils';
import { getPromoTags } from 'promo-tag-utils';
import { toggleModal } from 'dom-utils';

class CarouselComponent extends HTMLElement {
  constructor() {
    super();
    this.carousel = this.querySelector('[id^="Carousel-"]');
    this.carouselItems = this.querySelectorAll('[id^="Carousel-Item-"]');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.carousel || !this.nextButton) return;

    // Defer until after first paint so layout geometry is available for
    // isElementInViewport to correctly identify off-screen slides on init
    requestAnimationFrame(() => this._initPages());
    const resizeObserver = new ResizeObserver(entries => this._initPages());
    resizeObserver.observe(this.carousel);

    this.carousel.addEventListener('scroll', this._update.bind(this));
    this.prevButton.addEventListener('click', this._onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this._onButtonClick.bind(this));
  }

  _initPages() {
    const carouselItemArray = [...this.carouselItems];
    this.carouselItemsToShow = carouselItemArray.filter(
      element => element.clientWidth > 0
    );

    if (this.carouselItemsToShow.length < 2) return;
    this.carouselItemOffset =
      this.carouselItemsToShow[1].offsetLeft -
      this.carouselItemsToShow[0].offsetLeft;
    this.carouselItemsPerPage = Math.floor(
      (this.carousel.clientWidth - this.carouselItemsToShow[0].offsetLeft) /
        this.carouselItemOffset
    );
    this.totalPages =
      this.carouselItemsToShow.length - this.carouselItemsPerPage + 1;
    this._update();
  }

  _update() {
    // Derive the current 1-based page index from scroll position
    this.currentPage =
      Math.round(this.carousel.scrollLeft / this.carouselItemOffset) + 1;

    // Disable prev button when at the start
    if (this.carousel.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    // Disable next button when at the end; >= handles sub-pixel rounding
    if (
      this.carousel.scrollLeft + this.carousel.offsetWidth >=
      this.carousel.scrollWidth
    ) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }

    // Keep aria-hidden and tabindex in sync with slide visibility
    this._updateSlideAccessibility();
  }

  _updateSlideAccessibility() {
    const FOCUSABLE = 'a, button, input, select, textarea, [tabindex]';
    // Detect carousel mode by checking if all items share the same vertical
    // position. If any item wraps to a new row the layout is a grid, not a
    // carousel — regardless of whether the container still overflows horizontally.
    const firstTop = this.carouselItemsToShow[0]?.offsetTop;
    const lastTop =
      this.carouselItemsToShow[this.carouselItemsToShow.length - 1]?.offsetTop;
    const carouselActive =
      this.carouselItemsToShow.length >= 2 && firstTop === lastTop;

    const total = this.carouselItemsToShow.length;

    this.carouselItemsToShow.forEach((slide, index) => {
      // Check visibility relative to the carousel scroll container so this
      // works correctly when the carousel is inside a drawer or modal that
      // isn't itself fully within the window viewport.
      let visible = true;
      if (carouselActive) {
        const slideLeft = slide.offsetLeft;
        const slideRight = slideLeft + slide.clientWidth;
        const viewLeft = this.carousel.scrollLeft;
        const viewRight = viewLeft + this.carousel.clientWidth;
        const overlap =
          Math.min(slideRight, viewRight) - Math.max(slideLeft, viewLeft);
        visible = overlap / slide.clientWidth >= 0.5;
      }

      // "slide X of Y" label is only meaningful in carousel context
      if (carouselActive) {
        slide.setAttribute('aria-label', `slide ${index + 1} of ${total}`);
      } else {
        slide.removeAttribute('aria-label');
      }

      if (visible) {
        slide.removeAttribute('aria-hidden');
        slide
          .querySelectorAll(FOCUSABLE)
          .forEach(el => el.removeAttribute('tabindex'));
      } else {
        slide.setAttribute('aria-hidden', 'true');
        slide
          .querySelectorAll(FOCUSABLE)
          .forEach(el => el.setAttribute('tabindex', '-1'));
      }
    });
  }

  _onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.carousel.scrollLeft + step * this.carouselItemOffset
        : this.carousel.scrollLeft - step * this.carouselItemOffset;
    this.carousel.scrollTo({
      left: this.slideScrollPosition,
      behavior: 'smooth'
    });
  }
}

if (!customElements.get('carousel-component')) {
  customElements.define('carousel-component', CarouselComponent);
}

class ProductCard extends HTMLElement {
  // Specify observed attributes so that attributeChangedCallback fires
  static get observedAttributes() {
    return ['product', 'color', 'collections'];
  }

  constructor() {
    super();

    this.addToCart = this.addToCart.bind(this);
    this._product = null;
    this._selectedColor = null;
    const collectionsAttr = this.getAttribute('collections');
    this._productCollections = JSON.parse(collectionsAttr);
  }
  set selectedColor(color) {
    this._selectedColor = color;
    this._addProductOptions();
  }
  get selectedColor() {
    return this._selectedColor;
  }
  attributeChangedCallback(attr, oldVal, newVal) {
    if (newVal === 'null' || newVal === 'undefined') return;
    if (oldVal !== newVal) {
      if (attr === 'product') {
        this._product = JSON.parse(newVal);
      }
      if (attr === 'color') {
        this._selectedColor = newVal;
      }
      if (attr === 'collections') {
        this._productCollections = JSON.parse(newVal);
      }
      if (this._product) {
        this._addColorSwatches();
        this._addProductOptions();
        this._addPromoTags();
      }
    }
  }

  _addPromoTags() {
    if (!this._product.tags) return;
    getPromoTags();
  }

  _addColorSwatches() {
    // Check for tags that should disable colors
    if (
      !Array.isArray(this._product?.tags) ||
      !Array.isArray(this._product.options) ||
      !Array.isArray(this._product.variants) ||
      this._product.tags.includes('hide_quick_colors')
    )
      return;
    const colorOptionIndex = this._product.options.findIndex(
      option => String(option).toLowerCase() === 'color'
    );

    if (colorOptionIndex < 0) return;
    const colorOption = `option${colorOptionIndex + 1}`;
    let colors = [
      ...new Set(this._product.variants.map(variant => variant?.[colorOption]))
    ];
    // remove discontinued variants swatches
    colors = colors.filter(
      color =>
        typeof color === 'string' &&
        !color.toLowerCase().includes('discontinued')
    );
    const colorSwatches = colors.map(color => {
      if (!this._selectedColor) this.selectedColor = color;
      return html`
        <a
          tabindex="0"
          role="button"
          aria-label="preview in ${color}"
          title="${color}"
          class="swatch ${cssToken(color)} ${this._selectedColor === color
            ? 'active'
            : ''}"
          data-color="${color}"
          ><span class="swatch-color-square"></span
        ></a>
      `;
    });
    let colorImages = {};
    let colorIds = {};
    colors.forEach(color => {
      const colorVariant = this._product.variants.find(
        variant =>
          typeof variant?.title === 'string' && variant.title.includes(color)
      );
      // match the colors to their featured image
      if (colorVariant?.featured_image?.src) {
        colorImages[color] = colorVariant?.featured_image?.src;
      }
      // match the colors to their variant id
      if (colorVariant) {
        colorIds[color] = colorVariant.id;
      }
    });
    const productImageLink = this.querySelector('.product-card__image');
    const productName = this.querySelector('.product-card__title')?.innerText;
    if (colorSwatches.length > 4) {
      this.querySelector('.product-card__colors').innerHTML =
        html`${colorSwatches}<a
            href="${safeUrl(productImageLink?.href, { fallback: '#' })}"
            aria-label="Visit ${productName} product page"
            class="more-colors"
          >
            <span class="more-colors-caret">&gt; </span>
          </a>`;
    } else {
      this.querySelector('.product-card__colors').innerHTML =
        html`${colorSwatches}`;
    }
    this.querySelectorAll('.swatch').forEach(swatch =>
      swatch.addEventListener('click', e => {
        this.querySelectorAll('.swatch').forEach(swatch =>
          swatch.classList.remove('active')
        );
        e.target.closest('.swatch').classList.add('active');
        this.selectedColor = e.target.closest('.swatch').dataset.color;

        this.querySelector('.product-card__image img').src =
          colorImages[e.target.closest('.swatch').dataset.color];
        this.querySelector('.product-card__image img').srcset =
          colorImages[e.target.closest('.swatch').dataset.color];

        // Update the product card link with the variant id
        // handles algolia product cards and regular product cards by just swapping the variant id
        productImageLink.href = productImageLink.href.replace(
          /variant=\d+/,
          `variant=${colorIds[this.selectedColor]}`
        );
      })
    );

    this.querySelectorAll('.swatch').forEach(swatch =>
      swatch.addEventListener('keydown', e => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          this.querySelectorAll('.swatch').forEach(swatch =>
            swatch.classList.remove('active')
          );
          e.target.closest('.swatch').classList.add('active');
          this.selectedColor = e.target.closest('.swatch').dataset.color;

          this.querySelector('.product-card__image img').src =
            colorImages[e.target.closest('.swatch').dataset.color];
          this.querySelector('.product-card__image img').srcset =
            colorImages[e.target.closest('.swatch').dataset.color];

          // Update the product card link with the variant id
          // handles algolia product cards and regular product cards by just swapping the variant id
          productImageLink.href = productImageLink.href.replace(
            /variant=\d+/,
            `variant=${colorIds[this.selectedColor]}`
          );
        }
      })
    );
  }
  _addProductOptions() {
    // Check for tags that should disable quick options
    if (
      !Array.isArray(this._product?.tags) ||
      !Array.isArray(this._product.variants) ||
      !Array.isArray(this._product.options)
    )
      return;
    if (
      this._product.tags.includes('product-bundle') ||
      this._product.tags.includes('hide_quick_cart') ||
      this._product.tags.includes('printful-custom')
    )
      return;
    const productTitle = this._product.title;
    const productPrice = (this._product.price / 100).toFixed(2);
    const allVariants = this._product.variants.filter(
      variant => variant && typeof variant === 'object'
    );
    let optionButtons = [];
    const sizeMapping = {
      's': 'small',
      'm': 'medium',
      'l': 'large',
      'xl': 'extra large',
      '2xl': 'extra extra large',
      '3xl': 'extra extra extra large'
    };

    const hasOneSizeOption = allVariants.every(
      variant =>
        (Array.isArray(variant.options) &&
          variant.options.includes('One Size')) ||
        String(variant.title).includes('One Size')
    );
    const onlyHasColorOption =
      this._product.options.length === 1 &&
      this._product.options.includes('Color');
    // If single variant or uses One Size use Add To Bag
    if (allVariants.length === 1) {
      const variant = allVariants[0];
      if (variant.available) {
        const desktopATCButton = html`
          <a
            tabindex="0"
            role="button"
            title="add ${productTitle} to your bag"
            aria-label="add ${productTitle} to your bag"
            class="add-to-cart"
            data-product-title="${productTitle}"
            data-product-price="${productPrice}"
            data-variant-id="${variant.id}"
            data-option
            data-available="true"
            >Add To Bag</a
          >
        `;
        const mobileATCButton = html`
          <a
            tabindex="0"
            role="button"
            title="add to your bag"
            aria-label="add ${productTitle} to your bag"
            class="add-to-cart"
            data-product-title="${productTitle}"
            data-product-price="${productPrice}"
            data-variant-id="${variant.id}"
            data-option
            data-available="true"
            >+</a
          >
        `;
        optionButtons.push(desktopATCButton);
        // desktop
        this.querySelector('.product-card__options').innerHTML =
          desktopATCButton;
        // mobile < 768px
        this.querySelector('.product-card__cta').innerHTML = mobileATCButton;
      }
    } else {
      let variants = allVariants;
      let sizeOptionIndex = this._product.options.findIndex(
        option => String(option).toLowerCase() === 'size'
      );
      let colorOptionIndex = this._product.options.findIndex(
        option => String(option).toLowerCase() === 'color'
      );
      // If color option filter variants by selected color
      if (this._selectedColor) {
        variants = allVariants.filter(
          variant =>
            variant[`option${colorOptionIndex + 1}`] === this._selectedColor
        );
      }

      variants.forEach(variant => {
        let title = variant.title;
        // If option is size use option name, else use variant name
        if (sizeOptionIndex >= 0 && colorOptionIndex >= 0)
          title = variant[`option${sizeOptionIndex + 1}`];
        title = String(title ?? '');

        // Use the size mapping for aria-label and title if applicable
        const sizeKey = title.toLowerCase();
        const ariaLabelSize = Object.hasOwn(sizeMapping, sizeKey)
          ? sizeMapping[sizeKey]
          : title;
        const displayTitle =
          ariaLabelSize.charAt(0).toUpperCase() + ariaLabelSize.slice(1); // Capitalize the first letter
        const available = variant.available ? 'true' : 'false';

        optionButtons.push(html`
          <a
            tabindex="0"
            role="button"
            title="${displayTitle}"
            class="option"
            data-product-title="${productTitle}"
            data-product-price="${productPrice}"
            data-variant-id="${variant.id}"
            data-option
            data-available="${available}"
            aria-label="add ${productTitle} in ${ariaLabelSize} to your bag"
            >${title}</a
          >
        `);
        if (hasOneSizeOption || onlyHasColorOption) {
          const button = html`
            <a
              tabindex="0"
              role="button"
              title="add ${productTitle} to your bag"
              aria-label="add ${productTitle} to your bag"
              class="add-to-cart"
              data-product-title="${productTitle}"
              data-variant-id="${variant.id}"
              data-option
              data-available="${available}"
              >+</a
            >
          `;
          // mobile atc
          this.querySelector('.product-card__cta').innerHTML = button;
        }
      });
    }

    // Add options to form for desktop
    this.querySelector('.product-card__options').innerHTML =
      html`${optionButtons}`;
    // If not single variant add messaging
    if (allVariants.length > 1) {
      const parent = this.querySelector('.product-card__options');
      const p = document.createElement('p');
      p.classList.add('choose');
      p.innerHTML =
        optionButtons.length === 1
          ? 'Add To Bag'
          : 'Choose Option To Add To Bag';
      parent.insertBefore(p, parent.firstChild);
      if (!hasOneSizeOption && !onlyHasColorOption)
        // mobile
        this.querySelector('.product-card__cta').innerHTML = html`
          <a
            tabindex="0"
            role="button"
            aria-label="open the modal to choose your size"
            title="Add To Bag"
            class="add-to-cart"
            data-modal-trigger
            data-product-title="${productTitle}"
            >+</a
          >
        `;
    }

    // Add to cart on click event or keydown event
    this.querySelectorAll('[data-option]').forEach(option => {
      option.addEventListener('click', this.addToCart);
      option.addEventListener('keydown', e => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          this.addToCart(e);
        }
      });
    });
    // open the options modal
    this.querySelector('[data-modal-trigger]')?.addEventListener(
      'click',
      event => {
        event.preventDefault();
        event.stopPropagation();
        toggleModal('product-modal', {
          product: this._product,
          color: this._selectedColor
        });
      }
    );
    // Add a keydown event listener to handle Space and Enter
    this.querySelector('[data-modal-trigger]')?.addEventListener(
      'keydown',
      event => {
        // Check if the pressed key is Space or Enter
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault(); // Prevent scrolling when Space is pressed
          event.stopPropagation();
          toggleModal('product-modal', {
            product: this._product,
            collections: this._productCollections,
            color: this._selectedColor
          });
        }
      }
    );
  }
  addToCart(e) {
    e.preventDefault();
    const element = e.target.closest('[data-option]');
    if (element.dataset.available === 'true') {
      const variantId = element.dataset.variantId;
      const options = {
        id: variantId,
        quantity: 1,
        properties: this._product?.properties || {},
        option: element.innerText
      };
      BSS.Components.QuickCart.addItemToCart(options, element);
    }
    // Add to cart events
    // Twitter -- desktop product cards

    if (typeof twq !== 'undefined') {
      twq('event', 'tw-ohuni-ohv1q', {
        value: (this._product.price / 100).toFixed(2).toString(),
        currency: 'USD',
        contents: [
          {
            content_name: this._product.title,
            content_group_id: this._product.id
          }
        ]
      });
    }
    // Klaviyo -- desktop product cards
    if (this._product) {
      let klaviyoItem = {
        Name: this._product?.title ?? '',
        ProductID: this._product?.id,
        Categories: this._productCollections || this._product?.collections,
        ImageURL: `https:${this._product?.images[0]}`,
        URL: `https://shop.oldrow.net/products/${this._product?.handle}`,
        Brand: this._product?.vendor ?? '',
        Tags: this._product?.tags,
        rebuyProduct: this._product?.rebuyProduct ? true : false
      };

      // klaviyo event
      if (window.BSSConsent?.isGranted('marketing')) {
        _learnq.push(['track', 'Added to Cart', klaviyoItem]);
      }

      // dataLayer event
      if (this._product.rebuyProduct) {
        dataLayer.push({
          event: 'rebuyPDP Rec Widget Add to Cart',
          rebuyProductID: this._product.id,
          rebuyProductName: this._product.handle
        });
      }
    }
  }
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}
class ShowMore extends HTMLElement {
  constructor() {
    super();
    this.content = null;
    this.button = null;
    this.expanded = false;
  }

  connectedCallback() {
    this.content = this.querySelector('.about-the-store__content');
    this.button = this.querySelector('.about-the-store__button');
    if (!this.content || !this.button) return;

    this.button.addEventListener('click', () => this.toggle());
    this.update();
  }

  toggle() {
    this.expanded = !this.expanded;
    this.update();
  }

  update() {
    if (this.expanded) {
      this.content.classList.add('about-the-store__content--expanded');
      this.button.textContent = 'See Less';
    } else {
      this.content.classList.remove('about-the-store__content--expanded');
      this.button.textContent = 'See More';
    }
  }
}

if (!customElements.get('show-more')) {
  customElements.define('show-more', ShowMore);
}
